/** The one state rule for a form field (SA-1196, spec §5.1): computed from
 * the served pre-fill and the current value on every render; no `touched`. */
import type { FieldPrefill, FieldSourceKind } from '@/types/process'

export type FieldState = 'required' | 'prefilled' | 'overridden' | 'entered' | 'empty'
export type SourceTone = 'role-ai' | 'role-automation' | 'role-person'

/** The core's `_is_blank`: null, undefined, '' and []; `false` is a value. */
export function isBlank(value: unknown): boolean {
  return value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)
}

function sorted(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sorted)
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>
    return Object.fromEntries(Object.keys(record).sort().map((k) => [k, sorted(record[k])]))
  }
  return value
}

function canonical(value: unknown): string {
  try { return JSON.stringify(sorted(value)) ?? '' } catch { return '' }
}

/** Blanks are one nothing; a scalar compares as the text its control shows;
 * an object or a list as canonical JSON (keys sorted). */
export function sameValue(a: unknown, b: unknown): boolean {
  if (isBlank(a) && isBlank(b)) return true
  if (isBlank(a) || isBlank(b)) return false
  const scalarA = typeof a !== 'object', scalarB = typeof b !== 'object'
  if (scalarA && scalarB) return String(a) === String(b)
  if (scalarA !== scalarB) return false
  const ca = canonical(a)
  return ca !== '' ? ca === canonical(b) : Object.is(a, b)
}

export function fieldState(field: { optional?: boolean; prefill?: FieldPrefill | null }, value: unknown): FieldState {
  const blank = isBlank(value)
  if (blank && !field.optional) return 'required'
  const prefill = field.prefill ?? null
  if (prefill) return sameValue(value, prefill.value) ? 'prefilled' : 'overridden'
  return blank ? 'empty' : 'entered'
}

/** The hand that wrote the value, in the SA-459 role tokens (spec D8). */
export function sourceTone(kind: FieldSourceKind | string | null | undefined): SourceTone {
  if (kind === 'ai' || kind === 'proposal') return 'role-ai'
  if (kind === 'automation' || kind === 'event') return 'role-automation'
  return 'role-person'
}
